import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import { ArrowLeft } from 'lucide-react';
import { associateService } from '../services/associateService';
import { cepService } from '../services/cepService';
import { Associate, AssociateFormData, Plan } from '../types';
import { removeMask, formatCurrency } from '../utils/formatters';
import { maskCPF, maskPhone, maskCEP } from '../utils/masks';
import { validateEmail } from '../utils/validators';
import api from '../services/api';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://fabio-ubuntu:8082/api';

export const AssociateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchingCEP, setSearchingCEP] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);

  const [formData, setFormData] = useState<AssociateFormData>({
    nome: '',
    cpf: '',
    data_nascimento: '',
    data_adesao: new Date().toISOString().split('T')[0], // Data atual como padrão
    email: '',
    telefone: '',
    celular: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    foto: null,
    status: 'ativo',
    payment_day: 10,
    plan_id: undefined,
    is_hospital_employee: false,
    employee_cpf: '',
    employee_registration: '',
  });

  useEffect(() => {
    loadAvailablePlans();
    if (isEditing) {
      fetchAssociate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAvailablePlans = async () => {
    try {
      const response = await api.get('/plans/active');
      setAvailablePlans(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar planos disponíveis');
    }
  };

  const fetchAssociate = async () => {
    setLoading(true);
    try {
      const associate = await associateService.getById(Number(id));

      // Função para extrair apenas a data no formato YYYY-MM-DD
      const extractDate = (dateString: string | undefined) => {
        if (!dateString) return '';
        return dateString.split(' ')[0]; // Pega apenas a parte da data
      };

      setFormData({
        nome: associate.nome || associate.name || '',
        cpf: associate.cpf || '',
        data_nascimento: extractDate(associate.data_nascimento || associate.birth_date),
        data_adesao: extractDate(associate.data_adesao) || new Date().toISOString().split('T')[0],
        email: associate.email || '',
        telefone: associate.telefone || associate.phone || '',
        celular: associate.celular || '',
        cep: associate.cep || '',
        endereco: associate.endereco || associate.address || '',
        numero: associate.numero || '',
        complemento: associate.complemento || '',
        bairro: associate.bairro || '',
        cidade: associate.cidade || '',
        estado: associate.estado || '',
        foto: null,
        status: associate.status || (associate.active ? 'ativo' : 'inativo'),
        payment_day: associate.payment_day || 10,
        is_hospital_employee: associate.is_hospital_employee || false,
        employee_cpf: associate.employee_cpf || '',
        employee_registration: associate.employee_registration || '',
      });

      if (associate.foto) {
        setPhotoPreview(associate.foto);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          'Erro ao carregar dados do associado. Por favor, tente novamente.';
      setError(errorMessage);
      console.error('Associate fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkEmailExists = async (email: string) => {
    if (!validateEmail(email)) {
      return; // Não verifica se o email é inválido
    }

    // Ao editar, não verificar se o email não mudou
    if (isEditing) {
      return;
    }

    setCheckingEmail(true);
    setEmailExists(false);

    try {
      const response = await axios.get(`${API_URL}/associados/check-email/${email}`);

      if (response.data.exists) {
        setEmailExists(true);
        setError('Este e-mail já está cadastrado no sistema');

        // Mostra toast de erro
        toast.error('Este e-mail já está cadastrado no sistema!', {
          duration: 4000,
          position: 'top-center',
          icon: '⚠️',
          style: {
            background: '#ef4444',
            color: '#fff',
            fontWeight: '500',
            padding: '16px',
            borderRadius: '10px',
          },
        });
      } else {
        setEmailExists(false);
      }
    } catch (err: any) {
      console.error('Erro ao verificar e-mail:', err);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleCEPSearch = async (cep: string) => {
    const cleanCEP = removeMask(cep);
    if (cleanCEP.length !== 8) return;

    setSearchingCEP(true);
    try {
      const data = await cepService.buscarCep(cleanCEP);
      if (data && !data.erro) {
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf,
        }));
      }
    } catch (err) {
      console.error('CEP search error:', err);
    } finally {
      setSearchingCEP(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, foto: file }));

      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validar se o email já existe (apenas ao criar novo associado)
    if (!isEditing && emailExists) {
      setError('Este e-mail já está cadastrado no sistema');
      return;
    }

    setLoading(true);

    try {
      // Limpar máscaras antes de enviar
      const dataToSubmit = {
        ...formData,
        cpf: removeMask(formData.cpf),
        telefone: removeMask(formData.telefone),
        celular: formData.celular ? removeMask(formData.celular) : '',
        cep: removeMask(formData.cep),
      };

      if (isEditing) {
        await associateService.update(Number(id), dataToSubmit);
        setSuccess('Associado atualizado com sucesso!');
      } else {
        await associateService.create(dataToSubmit);
        setSuccess('Associado criado com sucesso!');
      }

      setTimeout(() => {
        navigate('/associados');
      }, 1500);
    } catch (err: any) {
      console.error('Associate save error:', err);

      // Tratar erros de validação do Laravel
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(`Erro: ${err.message}`);
      } else {
        setError(`Erro ao ${isEditing ? 'atualizar' : 'criar'} associado. Por favor, verifique os dados e tente novamente.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    let newValue: any = value;

    // Lidar com checkboxes
    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;

      // Se desmarcar "é funcionário do hospital", limpar os campos relacionados
      if (name === 'is_hospital_employee' && !newValue) {
        setFormData({
          ...formData,
          [name]: newValue,
          employee_cpf: '',
          employee_registration: '',
        });
        return;
      }
    } else if (name === 'cpf' || name === 'employee_cpf') {
      // Aplicar máscaras
      newValue = maskCPF(value);
    } else if (name === 'telefone' || name === 'celular') {
      newValue = maskPhone(value);
    } else if (name === 'cep') {
      newValue = maskCEP(value);
      if (removeMask(newValue).length === 8) {
        handleCEPSearch(newValue);
      }
    } else if (name === 'plan_id') {
      newValue = value ? Number(value) : undefined;
    }

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Toaster />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/associados')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Associado' : 'Novo Associado'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing
              ? 'Atualize as informações do associado'
              : 'Preencha os dados do novo associado'}
          </p>
        </div>

        {error && (
          <Alert type="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" className="mb-6">
            {success}
          </Alert>
        )}

        <Card>
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Foto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foto do Associado
              </label>
              <div className="flex items-center gap-4">
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Foto preview"
                    className="w-24 h-24 object-cover rounded-full border border-gray-300"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>

            {/* Dados Pessoais */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Dados Pessoais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Nome Completo *"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  placeholder="Nome completo do associado"
                />

                <Input
                  label="CPF *"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  required
                  placeholder="000.000.000-00"
                  maxLength={14}
                />

                <Input
                  label="Data de Nascimento *"
                  name="data_nascimento"
                  type="date"
                  value={formData.data_nascimento}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="Data de Adesão *"
                  name="data_adesao"
                  type="date"
                  value={formData.data_adesao}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="Melhor dia para pagamento"
                  name="payment_day"
                  type="number"
                  value={formData.payment_day}
                  onChange={handleChange}
                  min="1"
                  max="31"
                  placeholder="10"
                />

                <Input
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => checkEmailExists(formData.email)}
                  required
                  placeholder="email@exemplo.com"
                  disabled={checkingEmail}
                />
              </div>
            </div>

            {/* Vínculo com Hospital */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Vínculo com Hospital
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_hospital_employee"
                    name="is_hospital_employee"
                    checked={formData.is_hospital_employee || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="is_hospital_employee"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    É funcionário do hospital?
                  </label>
                </div>

                {formData.is_hospital_employee && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6">
                    <Input
                      label="CPF do Funcionário *"
                      name="employee_cpf"
                      value={formData.employee_cpf || ''}
                      onChange={handleChange}
                      required
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />

                    <Input
                      label="Matrícula *"
                      name="employee_registration"
                      value={formData.employee_registration || ''}
                      onChange={handleChange}
                      required
                      placeholder="Número da matrícula"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Plano - Somente para novos cadastros */}
            {!isEditing && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Plano de Benefícios
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecione o Plano (Opcional)
                  </label>
                  <select
                    name="plan_id"
                    value={formData.plan_id || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Nenhum plano (adicionar depois)</option>
                    {availablePlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.nome} - {formatCurrency(plan.valor)}/mês
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Você pode adicionar ou modificar planos após criar o associado
                  </p>
                </div>
              </div>
            )}

            {/* Contato */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informações de Contato
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Telefone *"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />

                <Input
                  label="Celular"
                  name="celular"
                  value={formData.celular}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Endereço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="CEP *"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  required
                  placeholder="00000-000"
                  maxLength={9}
                  disabled={searchingCEP}
                />

                <div className="md:col-span-2">
                  <Input
                    label="Endereço *"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleChange}
                    required
                    placeholder="Rua, Avenida, etc."
                    disabled={searchingCEP}
                  />
                </div>

                <Input
                  label="Número *"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  required
                  placeholder="123"
                />

                <Input
                  label="Complemento"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                  placeholder="Apto, Bloco, etc."
                />

                <Input
                  label="Bairro *"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  required
                  placeholder="Nome do bairro"
                  disabled={searchingCEP}
                />

                <Input
                  label="Cidade *"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  required
                  placeholder="Nome da cidade"
                  disabled={searchingCEP}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    required
                    disabled={searchingCEP}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Selecione o estado</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status do Associado *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="suspenso">Suspenso</option>
              </select>
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-6 border-t">
              <Button
                type="submit"
                disabled={loading || searchingCEP}
              >
                {loading
                  ? isEditing
                    ? 'Salvando...'
                    : 'Criando...'
                  : isEditing
                  ? 'Salvar Alterações'
                  : 'Criar Associado'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/associados')}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
